// src/components/workspace/shell/IncidentCommandBar.js

import { useEffect, useState } from "react";
import {
  ArrowLeft,
  ArrowRightCircle,
  Clock,
  PanelLeft,
  PanelRight,
  Rows2,
  Rows3,
  Timer,
} from "lucide-react";
import { getStatusMeta } from "../../../constants/incidentStatus";
import { formatDuration } from "../../../utils/duration";

/**
 * The single persistent header for an incident.
 *
 * Everything a participant needs to know at a glance without scrolling: which
 * incident, how bad it is, what state it is in, how long it has been running,
 * who else is here, and the one action that moves it forward. It replaces the
 * previous stack of a page header, a duplicate title toolbar, and a separate
 * call card that all showed overlapping information.
 */

function PresenceDot({ online }) {
  return (
    <span
      className={`inline-block h-2 w-2 rounded-full ${
        online ? "bg-emerald-500" : "bg-slate-300"
      }`}
      aria-hidden="true"
    />
  );
}

function Metric({ icon: Icon, label, value, title }) {
  return (
    <div
      className="flex items-center gap-1.5 text-xs text-slate-600"
      title={title}
    >
      <Icon className="h-3.5 w-3.5 text-slate-400" aria-hidden="true" />
      <span className="text-slate-500">{label}</span>
      <span className="font-semibold tabular-nums text-slate-800">{value}</span>
    </div>
  );
}

export default function IncidentCommandBar({
  incidentRef,
  title,
  status,
  severity,
  viewerRole,
  otherParticipantName,
  otherParticipantOnline,
  openedAt,
  sessionMs,
  sessionInProgress,
  primaryAction,
  secondaryActions = [],
  onBack,
  onToggleContext,
  onToggleComms,
  contextOpen,
  commsOpen,
  unreadCount = 0,
  nextAction,
  nextActionOwner,
  isCompact,
  onToggleDensity,
}) {
  const statusMeta = getStatusMeta(status, viewerRole);

  // "You" is more actionable than a role name when the ball is in your court.
  const waitingOnSelfKey = viewerRole === "client" ? "client" : "expert";

  // Elapsed time is a live counter, so it ticks without needing new data.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 30000);
    return () => window.clearInterval(id);
  }, []);

  const elapsedMs = openedAt ? now - openedAt : null;
  const PrimaryIcon = primaryAction?.icon;

  return (
    <header className="flex-shrink-0 border-b border-slate-200 bg-white">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-3 py-2.5 sm:px-4">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
          aria-label="Back to dashboard"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>

        <button
          type="button"
          onClick={onToggleContext}
          aria-pressed={contextOpen}
          className={`inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg transition focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            contextOpen
              ? "bg-slate-100 text-slate-800"
              : "text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          }`}
          aria-label={contextOpen ? "Hide incident context" : "Show incident context"}
        >
          <PanelLeft className="h-4 w-4" />
        </button>

        <div className="flex min-w-0 flex-1 items-center gap-2.5">
          <span className="hidden flex-shrink-0 font-mono text-xs tracking-tight text-slate-400 sm:inline">
            {incidentRef}
          </span>
          <h1 className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-900 sm:text-base">
            {title}
          </h1>
        </div>

        <div className="flex flex-shrink-0 items-center gap-1.5">
          <span
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${severity.chipClass}`}
            title={severity.description}
          >
            <span
              className={`h-1.5 w-1.5 rounded-full ${severity.dotClass}`}
              aria-hidden="true"
            />
            {severity.label}
          </span>
          <span
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${statusMeta.chipClass}`}
          >
            <span
              className={`h-1.5 w-1.5 rounded-full ${statusMeta.dotClass}`}
              aria-hidden="true"
            />
            {statusMeta.label}
          </span>
        </div>

        <div className="flex flex-shrink-0 items-center gap-2">
          {secondaryActions.map((action) => (
            <button
              key={action.label}
              type="button"
              onClick={action.onClick}
              className="hidden rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:inline-flex"
            >
              {action.label}
            </button>
          ))}

          {primaryAction && (
            <button
              type="button"
              onClick={primaryAction.onClick}
              disabled={primaryAction.disabled}
              className={`inline-flex items-center gap-2 rounded-lg px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60 ${
                primaryAction.tone === "success"
                  ? "bg-emerald-600 hover:bg-emerald-700 focus:ring-emerald-500"
                  : "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500"
              }`}
            >
              {PrimaryIcon && <PrimaryIcon className="h-4 w-4" />}
              {primaryAction.label}
            </button>
          )}

          <button
            type="button"
            onClick={onToggleDensity}
            className="hidden h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 lg:inline-flex"
            aria-label={
              isCompact ? "Use comfortable spacing" : "Use compact spacing"
            }
            title={isCompact ? "Comfortable spacing" : "Compact spacing"}
          >
            {isCompact ? (
              <Rows3 className="h-4 w-4" />
            ) : (
              <Rows2 className="h-4 w-4" />
            )}
          </button>

          <button
            type="button"
            onClick={onToggleComms}
            aria-pressed={commsOpen}
            className={`relative inline-flex h-8 w-8 items-center justify-center rounded-lg transition focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              commsOpen
                ? "bg-slate-100 text-slate-800"
                : "text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            }`}
            aria-label={commsOpen ? "Hide communications" : "Show communications"}
          >
            <PanelRight className="h-4 w-4" />
            {unreadCount > 0 && !commsOpen && (
              <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-2xs font-bold text-white">
                {Math.min(unreadCount, 9)}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Operational strip: facts, not decoration. */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 border-t border-slate-100 bg-slate-50 px-3 py-1.5 sm:px-4">
        {elapsedMs != null && (
          <Metric
            icon={Clock}
            label="Open"
            value={formatDuration(elapsedMs)}
            title="Time since the incident was reported"
          />
        )}
        {sessionMs > 0 && (
          <Metric
            icon={Timer}
            label={sessionInProgress ? "Session (live)" : "Session"}
            value={formatDuration(sessionMs)}
            title="Total live session time on this incident"
          />
        )}
        {otherParticipantName && (
          <div className="flex items-center gap-1.5 text-xs">
            <PresenceDot online={otherParticipantOnline} />
            <span className="text-slate-500">
              {viewerRole === "client" ? "Expert" : "Customer"}
            </span>
            <span className="font-semibold text-slate-800">
              {otherParticipantName}
            </span>
            <span className="text-slate-400">
              {otherParticipantOnline ? "online" : "offline"}
            </span>
          </div>
        )}

        {nextAction && (
          <div
            className="flex min-w-0 items-center gap-1.5 text-xs"
            title={nextAction}
          >
            <ArrowRightCircle className="h-3.5 w-3.5 flex-shrink-0 text-blue-500" />
            <span className="text-slate-500">
              {nextActionOwner === waitingOnSelfKey
                ? "You"
                : nextActionOwner === "client"
                ? "Customer"
                : "Expert"}
            </span>
            <span className="truncate font-semibold text-slate-800">
              {nextAction}
            </span>
          </div>
        )}
      </div>
    </header>
  );
}
